import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { COMPANY } from "@/lib/config";

export default function Confidentialite() {
    return (
        <main className="bg-cream text-ink min-h-screen" data-testid="confidentialite-page">
            <header className="border-b border-border bg-cream/90 backdrop-blur">
                <div className="max-w-4xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-sm text-stone2 hover:text-brand">
                        <ArrowLeft size={16} /> Retour au site
                    </Link>
                    <span className="font-heading text-base font-semibold tracking-tight">{COMPANY.name}</span>
                </div>
            </header>
            <article className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
                <span className="label-overline text-brand">RGPD</span>
                <h1 className="mt-4 font-heading text-4xl md:text-5xl font-light tracking-tighter text-ink leading-tight">
                    Politique de confidentialité
                </h1>
                <p className="mt-4 text-sm text-stone2">
                    Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
                </p>

                <div className="mt-12 space-y-10 text-base leading-relaxed text-ink/90">
                    <Section title="1. Responsable du traitement">
                        <p>
                            Le responsable du traitement des données collectées sur ce site est l'{COMPANY.name},
                            {" "}{COMPANY.address}.
                        </p>
                        <p>Contact : <a href={`mailto:${COMPANY.email}`} className="text-brand hover:underline break-all">{COMPANY.email}</a> · {COMPANY.phone}</p>
                    </Section>

                    <Section title="2. Données collectées">
                        <p>
                            Nous collectons uniquement les données que vous nous transmettez volontairement via :
                        </p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li><strong>Le formulaire de contact</strong> : nom, email, téléphone (facultatif), sujet, message, service concerné.</li>
                            <li><strong>Le formulaire d'inscription aux formations</strong> : nom complet, email, téléphone (facultatif), profil (particulier/pro), message.</li>
                        </ul>
                        <p className="mt-3 text-stone2">
                            Aucune donnée n'est collectée à votre insu et nous n'utilisons pas de cookies de suivi publicitaire.
                        </p>
                    </Section>

                    <Section title="3. Finalité du traitement">
                        <p>Vos données sont utilisées exclusivement pour :</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>Répondre à vos demandes de contact et de devis</li>
                            <li>Gérer les inscriptions aux formations</li>
                            <li>Vous tenir informé(e) du suivi de votre demande</li>
                        </ul>
                        <p className="mt-3">
                            Vos données ne sont <strong>jamais cédées, vendues ou louées</strong> à des tiers.
                        </p>
                    </Section>

                    <Section title="4. Base légale">
                        <p>
                            Le traitement de vos données est fondé sur votre consentement (art. 6.1.a du RGPD)
                            et sur l'exécution des mesures précontractuelles prises à votre demande
                            (art. 6.1.b du RGPD).
                        </p>
                    </Section>

                    <Section title="5. Durée de conservation">
                        <ul className="list-disc pl-6 space-y-2">
                            <li><strong>Demandes de contact non donnant pas suite</strong> : 12 mois.</li>
                            <li><strong>Données clients (devis, missions réalisées)</strong> : 10 ans (durée légale de conservation des documents commerciaux).</li>
                            <li><strong>Inscriptions aux formations</strong> : durée nécessaire à la formation + 5 ans.</li>
                        </ul>
                    </Section>

                    <Section title="6. Vos droits">
                        <p>Conformément au RGPD et à la loi Informatique et Libertés, vous disposez :</p>
                        <ul className="list-disc pl-6 space-y-2">
                            <li>D'un droit d'<strong>accès</strong> à vos données</li>
                            <li>D'un droit de <strong>rectification</strong></li>
                            <li>D'un droit à l'<strong>effacement</strong> (« droit à l'oubli »)</li>
                            <li>D'un droit à la <strong>limitation</strong> du traitement</li>
                            <li>D'un droit à la <strong>portabilité</strong> de vos données</li>
                            <li>D'un droit d'<strong>opposition</strong></li>
                        </ul>
                        <p className="mt-3">
                            Pour exercer ces droits, écrivez-nous à <a href={`mailto:${COMPANY.email}`} className="text-brand hover:underline break-all">{COMPANY.email}</a>.
                            Vous pouvez également déposer une réclamation auprès de la <a className="text-brand hover:underline" href="https://www.cnil.fr" target="_blank" rel="noreferrer">CNIL</a>.
                        </p>
                    </Section>

                    <Section title="7. Sécurité">
                        <p>
                            Vos données sont stockées sur des serveurs sécurisés. Nous mettons en œuvre les
                            mesures techniques et organisationnelles appropriées pour protéger vos données
                            contre tout accès, modification ou divulgation non autorisés.
                        </p>
                    </Section>

                    <Section title="8. Cookies">
                        <p>
                            Ce site n'utilise pas de cookies de traçage publicitaire. Seuls des cookies
                            techniques strictement nécessaires au fonctionnement du site (session, sécurité)
                            peuvent être déposés.
                        </p>
                    </Section>
                </div>
            </article>
        </main>
    );
}

function Section({ title, children }) {
    return (
        <section>
            <h2 className="font-heading text-2xl font-medium tracking-tight text-ink mb-4">{title}</h2>
            <div className="space-y-3">{children}</div>
        </section>
    );
}
